import React, { useCallback, useEffect, useMemo, useState } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Logo, BUSINESS_UNIT_ROLLWORKS } from '@nextroll/ar-style-base';

import { Chips } from '../Chips/Chips';


interface IProps {
    title: string;
    icon?: JSX.Element; // e.g. <i className='fa-solid fa-user' />
    items?: string[];
    setItems?: (items: string[]) => void;
    className?: { root?: string; body?: string };
    children?: React.ReactNode;
}

export const ExpandablePanel = (props: IProps) => {
    const { title, icon, items = [], setItems, className } = props;
    const [open, setOpen] = useState(false);

    /**
     * auto-collapse the panel if all selections are removed
     */
    useEffect(() => {
        items.length <= 0 && setOpen(false);
    }, [items.length]);

    const handleClear = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setItems?.([]);
    }, [setItems]);

    const rootClassName = useMemo(() => [
        'workflow-node-panel-filter ar-checklist-item hoverable',
        open && 'open',
        className?.root,
    ].filter(Boolean).join(' '), [open, className]);

    const ariaLabel = useMemo(() => `expandable-panel-${title}-${open ? 'open' : 'closed'}`, [title, open]);

    return (
        <div className={rootClassName} aria-label={ariaLabel}>
            <div className='ar-checklist-item-header' onClick={() => setOpen(!open)}>
                <div className='ar-checklist-item-header-title'>
                    {icon || (
                        <Logo
                            i18n={i18n}
                            businessUnit={BUSINESS_UNIT_ROLLWORKS}
                            height='16px'
                            wordmark={false}
                            disableRollWorksTheme
                        />
                    )}
                    {title}
                    {items.length > 0 && <Chips labels={[`${items.length}`]} onDelete={handleClear} />}
                </div>
                <span className='ar-checklist-item-header-caret'><i className='fas fa-angle-down fa-lg' /></span>
            </div>

            <div className={['ar-checklist-item-body', className?.body].filter(Boolean).join(' ')}>
                {props.children}
            </div>

            <div className='ar-checklist-item-footer'>
                <button type='button' className='btn btn-link' onClick={handleClear}>{i18n.gettext('Clear')}</button>
            </div>
        </div>
    );
};
